// LEGACY — safe to delete once old case copilot sessions stop mattering.
//
// Before field references became `<reference kind="field" .../>` mentions,
// "Rewrite with Copilot" prefixed the message text with a raw
// `<ref label="..." .../>` line. Messages stored in that format still exist
// in saved sessions; this module strips the prefix from the bubble text and
// renders it as a chip. Nothing produces the old format anymore, so to drop
// support: delete this file, its spec, and the call sites in
// `CaseCopilotWindow` (`parseLegacyFieldRef` + `LegacyFieldRefChip`).

import { Pill } from "src/base-components/Pill";

export type ParsedLegacyFieldRef = {
  /** Label of the leading legacy `<ref .../>` tag, if the message has one. */
  legacyRefLabel: string | null;
  /** Message content with the legacy prefix stripped. */
  text: string;
};

const decodeXmlEntities = (raw: string): string =>
  raw
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

const parseXmlAttributes = (s: string): Record<string, string> => {
  const attrs: Record<string, string> = {};
  let i = 0;
  while (i < s.length) {
    while (i < s.length && /\s/.test(s[i])) {
      i += 1;
    }
    if (i >= s.length) {
      break;
    }
    const keyMatch = /^(\w+)=/.exec(s.slice(i));
    if (!keyMatch) {
      break;
    }
    const key = keyMatch[1];
    i += keyMatch[0].length;
    if (s[i] !== '"') {
      break;
    }
    i += 1;
    const end = s.indexOf('"', i);
    if (end === -1) {
      break;
    }
    attrs[key] = decodeXmlEntities(s.slice(i, end));
    i = end + 1;
  }
  return attrs;
};

const LEADING_REF_PATTERN = /^<ref\s+([\s\S]*?)\/>\s*\n?([\s\S]*)$/;

export const parseLegacyFieldRef = (content: string): ParsedLegacyFieldRef => {
  const match = content.match(LEADING_REF_PATTERN);
  if (!match) {
    return { legacyRefLabel: null, text: content };
  }
  const attrs = parseXmlAttributes(match[1]);
  return { legacyRefLabel: attrs.label ?? null, text: match[2] };
};

/** Chip for the legacy field reference, shown next to the regular chips. */
export const LegacyFieldRefChip: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex min-w-0">
    <Pill size="sm" variant="gray" maxWidth>
      <Pill.Text>{label}</Pill.Text>
    </Pill>
  </div>
);
